import cn from 'classnames';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import {
  Navigation,
  A11y,
  FreeMode,
  Scrollbar,
  Mousewheel,
} from 'swiper/modules';
import { Transition } from 'react-transition-group';
import Heading from '@components/ui/Heading';
import IconWrapper from '@components/ui/Icons';
import {
  CategoryCarouselItem,
  CategoryCarouselType,
} from '@adapters/prismic/types/plp';
import CarouselSlide from '../CarouselSlide';
import TagManager from 'react-gtm-module';
import useObjectVisibility from '@hooks/useObjectVisibility';

import styles from './CategoryCarousel.module.scss';

import 'swiper/css';
import 'swiper/css/scrollbar';
import { getVisibleSlideIndexes } from '@utils/swiper';

interface CategoryCarouselProps {
  carousel: CategoryCarouselItem;
}

const CategoryCarousel = ({ carousel }: CategoryCarouselProps) => {
  const { t } = useTranslation('plp');

  const [isOutOfView, setIsOutOfView] = useState(false);
  const [viewAll, setViewAll] = useState(false);
  const [recommendationSwiper, setRecommendationSwiper] =
    useState<SwiperType | null>(null);
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);
  const [swiperSize, setSwiperSize] = useState(0);
  const [viewedSlides, setViewedSlides] = useState<number[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const carouselTransitionRef = useRef<HTMLDivElement>(null);
  const viewAllTransitionRef = useRef<HTMLDivElement>(null);

  const setFade = (swiper: SwiperType) => {
    if (swiper.allowSlideNext && swiper.allowSlidePrev) {
      setIsOutOfView(true);
    }
    setFadeLeft(!swiper.isBeginning);
    setFadeRight(!swiper.isEnd);
  };

  const handleViewAllClick = () => {
    setViewAll(!viewAll);
  };

  const generateTrackingData = useCallback(
    (slideLabel: string, i: number) => ({
      carousel_slide_name: slideLabel,
      carousel_type: carousel?.type,
      index: i,
    }),
    [carousel?.type]
  );

  const trackSlideView = useCallback(
    (visibleSlideIndexes: number[] = []) => {
      const untrackedSlideIndexes = visibleSlideIndexes.filter(
        (slide) => viewedSlides?.indexOf(slide) === -1
      );

      if (untrackedSlideIndexes.length > 0) {
        untrackedSlideIndexes.forEach((i) => {
          TagManager.dataLayer({
            dataLayer: {
              event: 'user_interaction',
              event_name: 'view_carousel_slide',
              interaction: generateTrackingData(carousel?.items[i]?.label, i),
            },
          });
        });

        setViewedSlides((currentSlides) => [
          ...currentSlides,
          ...untrackedSlideIndexes,
        ]);
      }
    },
    [carousel?.items, generateTrackingData, viewedSlides]
  );

  const [hasInitialTrackingFired, setInitialTrackingFired] = useState(false);
  const isCarouselVisible = useObjectVisibility(containerRef);

  useEffect(() => {
    if (recommendationSwiper && isCarouselVisible && !hasInitialTrackingFired) {
      trackSlideView(getVisibleSlideIndexes(recommendationSwiper));
      setInitialTrackingFired(true);
    }
  }, [
    isCarouselVisible,
    recommendationSwiper,
    trackSlideView,
    hasInitialTrackingFired,
  ]);

  const defaultStyle = {
    transition: `opacity ${300}ms ease-in-out`,
    opacity: 0,
  };

  const transitionStyles = {
    entering: { opacity: 0 },
    entered: { opacity: 1 },
    exiting: { opacity: 1 },
    exited: { opacity: 0 },
    unmounted: { opacity: 0 },
  };

  const slides = [
    ...carousel?.items?.map((item, i) => (
      <SwiperSlide
        key={item?.label}
        className={cn(styles.swiperSlide, {
          [styles.noImage]: !item?.image?.url,
        })}
      >
        <CarouselSlide
          slide={item}
          carouselType={carousel?.type}
          swiperSize={
            carousel?.type === CategoryCarouselType.featured ? swiperSize : null
          }
          headingWithoutSpaces={`${carousel?.heading?.replace(/ /g, '')}${i}`}
          index={i}
        />
      </SwiperSlide>
    )),
    // Placeholder slide so that the last slide doesn't get cut off when hovering on featured carousel
    carousel?.type === CategoryCarouselType.featured && (
      <SwiperSlide
        key="placeholder-slide"
        className={styles.placeholderSlide}
      ></SwiperSlide>
    ),
  ];

  const viewAllSlides = carousel?.items?.map((item, i) => (
    <div
      key={item?.label}
      className={cn(styles.swiperSlide, {
        [styles.noImage]: !item?.image?.url,
        [styles.viewAllSlide]: viewAll,
      })}
    >
      <CarouselSlide slide={item} carouselType={carousel?.type} index={i} />
    </div>
  ));

  return (
    <div className={styles.container} ref={containerRef}>
      <Heading semanticLevel="h2" styleLevel="4" className={styles.header}>
        {carousel?.heading}
      </Heading>
      {isOutOfView && (
        <button className={styles.viewAllButton} onClick={handleViewAllClick}>
          {viewAll
            ? t('plp:categoryCarousel.collapseAll')
            : t('plp:categoryCarousel.viewAll')}
          {!viewAll && (
            <IconWrapper
              icon="arrow-expand"
              width={14}
              height={10}
              classNames={styles.arrowIcon}
            />
          )}
          {viewAll && (
            <IconWrapper
              icon="arrow-collapse"
              width={14}
              height={7}
              classNames={styles.arrowIcon}
            />
          )}
        </button>
      )}
      {
        <Transition
          nodeRef={carouselTransitionRef}
          in={!viewAll}
          timeout={{ enter: 300, exit: 0 }}
          unmountOnExit
        >
          {(state) => (
            <div
              ref={carouselTransitionRef}
              style={{ ...defaultStyle, ...transitionStyles[state] }}
            >
              <button
                type="button"
                className={cn(styles.swipeButt, styles.swipeButtPrev, {
                  [styles.swiperLeftButtonHide]: !fadeLeft,
                })}
                onClick={() => recommendationSwiper?.slidePrev()}
              >
                <span className={styles.screenReaderOnly}>
                  {t('categoryCarousel.carousel.prev')}
                </span>
                <IconWrapper
                  icon="caret-left"
                  width={20}
                  classNames={styles.caretIcon}
                />
              </button>
              <Swiper
                modules={[Navigation, A11y, FreeMode, Scrollbar, Mousewheel]}
                className={styles.swiper}
                navigation={{
                  nextEl: styles.swipeButtNext,
                  prevEl: styles.swipeButtPrev,
                }}
                onSwiper={setRecommendationSwiper}
                onReachBeginning={setFade}
                onReachEnd={setFade}
                onInit={(swiper) => {
                  setFade(swiper);
                  swiper.updateSize();
                  setSwiperSize(swiper.width);
                }}
                onSlideChange={(swiper) => {
                  setFade(swiper);
                  trackSlideView(getVisibleSlideIndexes(swiper));
                }}
                spaceBetween={8}
                breakpoints={{
                  1088: {
                    spaceBetween: 24,
                  },
                }}
                direction={'horizontal'}
                slidesPerView={'auto'}
                freeMode={true}
                watchSlidesProgress={true}
                grabCursor={true}
                onResize={(swiper) => {
                  swiper.updateSize();
                  setSwiperSize(swiper.width);
                }}
              >
                {slides}
              </Swiper>
              <button
                type="button"
                className={cn(styles.swipeButt, styles.swipeButtNext, {
                  [styles.swipeRightButtHide]: !fadeRight,
                })}
                onClick={() => recommendationSwiper?.slideNext()}
              >
                <span className={styles.screenReaderOnly}>
                  {t('categoryCarousel.carousel.next')}
                </span>
                <IconWrapper
                  icon="caret-right"
                  width={20}
                  classNames={styles.caretIcon}
                />
              </button>
            </div>
          )}
        </Transition>
      }
      {
        <Transition
          nodeRef={viewAllTransitionRef}
          in={viewAll}
          timeout={{ enter: 300, exit: 0 }}
          unmountOnExit
        >
          {(state) => (
            <div
              ref={viewAllTransitionRef}
              style={{ ...defaultStyle, ...transitionStyles[state] }}
              className={cn(styles.viewAll, {
                [styles.viewAllGrid]:
                  carousel.type === CategoryCarouselType.featured,
                [styles.viewAllFlex]:
                  carousel.type !== CategoryCarouselType.featured,
                [styles.viewAllBrands]:
                  carousel.type === CategoryCarouselType.brands,
              })}
            >
              {viewAllSlides}
            </div>
          )}
        </Transition>
      }
    </div>
  );
};

export default CategoryCarousel;
